import type { OrgPickerTree } from '../../../lib/org-picker-tree';
import type { PickerGroup } from '../../../components/shared/EntityPicker';
import { tCalendar } from '../../../lib/calendar-i18n';

/**
 * 참석자 이름 — **서버 DTO 가 userId 만 준다** (`attendees: { userId, status, respondedAt }`).
 * 그래서 화면이 종전에 「#5 (ACCEPTED)」라고 그리고 있었다. 참석자를 «만들 수 있게» 되면
 * 그 글자가 곧바로 사용자 눈에 띄므로, 참석자를 넣는 판에서 함께 막는다.
 *
 * ⚠ **이것은 우회다.** 같은 라우트가 작성자에게는 이미 이름을 붙여 보낸다
 *   (`creator: { id, name, avatarUrl }`). 참석자에도 같은 꼴이 붙으면 이 모듈은 걷어낸다.
 *   · 만료 조건 — `CalendarEventDto.attendees` 에 `name` 이 생기면.
 *   · 그때 할 일 — 이 파일과 상세 모달의 조직도 읽기를 지우고 DTO 값을 그대로 쓴다.
 *
 * ⚠ 조직도는 **3+N 호출**이라 아무 때나 부르면 안 된다. 그래서 「모르는 사람이 있을 때만」
 *   부르도록 `needsOrgTree` 를 따로 둔다 — 참석자가 나 혼자인 일정(가장 흔하다)은 호출 0 이다.
 */

/** userId → 이름. 트리는 부서 → 팀 → 사람이라 하위까지 훑는다. */
export function indexPersonNames(tree: OrgPickerTree): ReadonlyMap<string, string> {
  const out = new Map<string, string>();
  const walk = (list: ReadonlyArray<PickerGroup>) => {
    for (const g of list) {
      for (const p of g.people ?? []) out.set(p.id, p.name);
      walk(g.children ?? []);
    }
  };
  walk(tree.groups);
  for (const p of tree.loose) out.set(p.id, p.name);
  return out;
}

export interface AttendeeNameSources {
  /** 서버가 이미 이름을 붙여 보내는 유일한 사람. */
  creator?: { id: string; name: string } | null;
  myUserId?: string | null;
  myName?: string | null;
  index?: ReadonlyMap<string, string> | null;
  /** 조직도를 읽는 중 — 「알 수 없는 사용자」로 단정하기 «전»의 상태. */
  loading?: boolean;
}

/**
 * ⭐ 「모르는 사람이 있나」 — 이 값이 거짓이면 조직도를 **부르지 않는다**.
 *   작성자와 나는 이름을 이미 알고 있어서, 그 둘뿐인 일정은 3+N 호출이 필요 없다.
 */
export function needsOrgTree(
  attendees: ReadonlyArray<{ userId: string }>,
  src: Pick<AttendeeNameSources, 'creator' | 'myUserId'>,
): boolean {
  const known = new Set([src.creator?.id, src.myUserId].filter((v): v is string => v != null));
  return attendees.some((a) => !known.has(a.userId));
}

/** 한 사람을 무엇으로 그리나. ⛔ 어느 갈래에서도 「#5」로 돌아가지 않는다. */
export function attendeeName(userId: string, src: AttendeeNameSources): string {
  if (src.creator && src.creator.id === userId) return src.creator.name;
  if (src.myUserId === userId && src.myName) return src.myName;
  const found = src.index?.get(userId);
  if (found) return found;
  // 아직 읽는 중이면 「없다」고 단정하지 않는다 — 곧 이름이 들어온다.
  return src.loading ? '…' : tCalendar('calendar.attendee.unknown');
}
